import { zodResolver } from "@hookform/resolvers/zod";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Button } from "@/app/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/app/components/ui/field";
import { Input } from "@/app/components/ui/input";
import type { ResumeContextValue } from "@/app/types/resume";

import tips from "../../../../assets/copy/tips";
import ErrorToast from "../../../../components/ErrorToast";
import Spinner from "../../../../components/Spinner";
import Tips from "../../../../components/Tips/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { dateObjToYMD } from "../../../../handlers/utilityFunctions";
import { PersonalInfo } from "../../../../models/resume/personalInfo";
import { StaticValue } from "../../../../seed/constants";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";

const personalProfileSchema = z.object({
	gender: z.string().min(1, "Select your gender!"),
	maritalStatus: z.string().min(1, "Select your marital status!"),
	// Date of birth is not validated; the legacy form allowed an empty value.
	dateOfBirth: z.string(),
});

type PersonalProfileFormValues = z.infer<typeof personalProfileSchema>;

interface PatchResponse {
	personlPrivateInfo: {
		gender: string;
		maritalStatus: string;
		dateOfBirth: Parameters<typeof dateObjToYMD>[0];
	};
}

interface PatchError {
	response: { data: { message: string | string[] } };
}

const selectClassName =
	"flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm transition-colors focus-visible:outline-hidden";

const PersonalProfile = () => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();

	const staticValue = new StaticValue();
	const patchSection = usePatchResumeSection();
	const staticValues = staticValue.getDefaultValues();
	const [serverErrors, setServerErrors] = useState<string[]>([]);

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;
	const { personalProfile } = resumeData.extras;

	const {
		formState: { errors },
		getValues,
		handleSubmit,
		register,
		reset,
	} = useForm<PersonalProfileFormValues>({
		resolver: zodResolver(personalProfileSchema),
		defaultValues: {
			gender: "",
			maritalStatus: "",
			dateOfBirth: "",
		},
	});

	useEffect(() => {
		const currentValues = getValues();
		reset({
			gender: personalProfile.gender || currentValues.gender || "",
			maritalStatus: personalProfile.maritalStatus || currentValues.maritalStatus || "",
			dateOfBirth: personalProfile.dateOfBirth || currentValues.dateOfBirth || "",
		});
	}, [getValues, reset, personalProfile]);

	//form submission handler
	const addProfile = (profile: PersonalProfileFormValues) => {
		const payload = new PersonalInfo(profile);
		const request = payload.getPersonalInfoRequest();

		const resumeId = cookies[staticValues.localStorageResumeId];

		patchSection.mutate({ resumeId, section: RESUME_SECTIONS.PERSONAL, data: request } as never, {
			onSuccess,
			onError,
		});
	};

	function onSuccess(data: PatchResponse) {
		const response = data.personlPrivateInfo;
		const date = dateObjToYMD(response.dateOfBirth);

		// Legacy quirk kept as-is: the context row stores the gender under `sex`.
		const profileData = {
			dateOfBirth: date,
			sex: response.gender,
			maritalStatus: response.maritalStatus,
		};

		setResumeData((prevState) => {
			return {
				...prevState,
				extras: {
					...prevState.extras,
					personalProfile: profileData as unknown as typeof prevState.extras.personalProfile,
				},
			};
		});

		navigate("/resume/extras");
	}

	function onError(error: Error) {
		const serverResponse = (error as unknown as PatchError).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	return (
		<div className="w-full md:max-w-xl">
			{serverErrors.map((item, index) => {
				return <ErrorToast error={item} key={index} />;
			})}
			<div className="mb-2 flex justify-end">
				<Tips content={tips.personalProfile} />
			</div>
			<div className="mb-3 text-gray-900">
				<p>Add additional personal details.</p>
			</div>

			<form noValidate onSubmit={handleSubmit(addProfile)}>
				<div className="grid grid-cols-2 gap-3">
					<Field className="mb-4 gap-1" data-invalid={!!errors.gender}>
						<FieldLabel htmlFor="personal-profile-gender">Gender</FieldLabel>
						<select
							id="personal-profile-gender"
							className={selectClassName}
							aria-invalid={!!errors.gender}
							aria-describedby={errors.gender ? "personal-profile-gender-error" : undefined}
							{...register("gender")}
						>
							<option disabled value="">
								Select Option
							</option>
							<option value="MALE">Male</option>
							<option value="FEMALE">Female</option>
						</select>
						<FieldError id="personal-profile-gender-error" className="text-error-default" errors={[errors.gender]} />
					</Field>
					<Field className="mb-4 gap-1" data-invalid={!!errors.maritalStatus}>
						<FieldLabel htmlFor="personal-profile-marital-status">Marital Status</FieldLabel>
						<select
							id="personal-profile-marital-status"
							className={selectClassName}
							aria-invalid={!!errors.maritalStatus}
							aria-describedby={errors.maritalStatus ? "personal-profile-marital-status-error" : undefined}
							{...register("maritalStatus")}
						>
							<option disabled value="">
								Select Option
							</option>
							<option value="SINGLE">Single</option>
							<option value="MARRIED">Married</option>
							{/* The server's MaritalStatus enum spells this member "SEPERATED".
							    Sending the correct spelling failed validation and blocked the
							    save, so the value matches the server and the label does not. */}
							<option value="SEPERATED">Separated</option>
						</select>
						<FieldError
							id="personal-profile-marital-status-error"
							className="text-error-default"
							errors={[errors.maritalStatus]}
						/>
					</Field>
				</div>

				<Field className="mb-4 gap-1" data-invalid={!!errors.dateOfBirth}>
					<FieldLabel htmlFor="personal-profile-date-of-birth">Date Of Birth</FieldLabel>
					<Input id="personal-profile-date-of-birth" type="date" {...register("dateOfBirth")} />
					<FieldError
						id="personal-profile-date-of-birth-error"
						className="text-error-default"
						errors={[errors.dateOfBirth]}
					/>
				</Field>

				<div className="mt-4 flex w-full items-center justify-end gap-2">
					<button
						type="button"
						onClick={() => {
							navigate("/resume/extras");
						}}
						className="cursor-pointer font-semibold text-primary-700"
					>
						Skip for now
					</button>
					<Button type="submit">
						{patchSection.isPending ? (
							<>
								Loading <Spinner />
							</>
						) : (
							"Continue"
						)}
					</Button>
				</div>
			</form>
		</div>
	);
};

export default PersonalProfile;
